window.getPdpThumbnailClickMode =
	window.getPdpThumbnailClickMode ||
	(() => document.querySelector('.product')?.dataset?.thumbnailClick || 'default');

/**
 * Full-screen PDP gallery (thumbnail_click_behavior: open_gallery).
 */
class PdpImageGallery {
	constructor() {
		this.root = document.getElementById('PdpImageGallery');
		if (!this.root) return;

		this.sectionId =
			document.querySelector('variant-selects')?.dataset?.section ||
			document.querySelector('product-info')?.dataset?.section ||
			document.querySelector('media-gallery')?.id?.replace('MediaGallery-', '');

		this.stage = this.root.querySelector('[data-pdp-gallery-stage]');
		this.mediaEl = this.root.querySelector('[data-pdp-gallery-media]');
		this.transformEl = this.root.querySelector('[data-pdp-gallery-transform]');
		this.mainImage = this.root.querySelector('[data-pdp-gallery-main]');
		this.videoEl = this.root.querySelector('[data-pdp-gallery-video]');
		this.thumbsEl = this.root.querySelector('[data-pdp-gallery-thumbs]');
		this.thumbsWrap = this.root.querySelector('[data-pdp-gallery-thumbs-wrap]');
		this.thumbsFade = this.root.querySelector('[data-pdp-gallery-thumbs-fade]');
		this.hint = this.root.querySelector('[data-pdp-gallery-hint]');

		this.items = [];
		this.activeIndex = 0;
		this.scale = 1;
		this.translate = { x: 0, y: 0 };
		this.isDragging = false;
		this.isPinching = false;
		this.dragStart = { x: 0, y: 0, translateX: 0, translateY: 0 };
		this.pinchStartDistance = 0;
		this.pinchStartScale = 1;

		this.minScale = 1;
		this.maxScale = 3;
		this.scaleStep = 0.25;

		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.updateThumbScrollFade = this.updateThumbScrollFade.bind(this);

		this.bindEvents();
	}

	bindEvents() {
		this.root.querySelectorAll('[data-pdp-gallery-close]').forEach((el) => {
			el.addEventListener('click', () => this.close());
		});

		this.root.querySelector('[data-pdp-gallery-zoom-in]')?.addEventListener('click', () => this.zoomIn());
		this.root.querySelector('[data-pdp-gallery-zoom-out]')?.addEventListener('click', () => this.zoomOut());

		this.root.querySelector('[data-pdp-gallery-hint-dismiss]')?.addEventListener('click', () => {
			this.hint?.setAttribute('hidden', '');
		});

		this.thumbsEl?.addEventListener('click', (event) => {
			const btn = event.target.closest('[data-pdp-gallery-thumb-index]');
			if (!btn) return;
			this.setIndex(Number(btn.dataset.pdpGalleryThumbIndex));
		});

		this.thumbsWrap?.addEventListener('scroll', this.updateThumbScrollFade, { passive: true });
		window.addEventListener('resize', this.updateThumbScrollFade);

		this.stage?.addEventListener('mousedown', (event) => this.onDragStart(event));
		this.stage?.addEventListener('touchstart', this.onTouchStart, { passive: false });
		this.stage?.addEventListener('touchmove', this.onTouchMove, { passive: false });
		this.stage?.addEventListener('touchend', this.onTouchEnd);
		this.stage?.addEventListener('touchcancel', this.onTouchEnd);
		window.addEventListener('mousemove', (event) => this.onDragMove(event));
		window.addEventListener('mouseup', () => this.onDragEnd());

		document.addEventListener('keydown', (event) => {
			if (!this.isOpen()) return;
			if (event.key === 'Escape') this.close();
		});
	}

	isOpen() {
		return this.root && !this.root.hidden;
	}

	getSlideEl(mediaId) {
		const viewer = document.querySelector(`#GalleryViewer-${this.sectionId}`);
		return (
			viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
			document.querySelector(`[data-media-id="${mediaId}"]`)
		);
	}

	setTransformVisible(visible) {
		if (!this.transformEl) return;
		this.transformEl.hidden = !visible;
		this.transformEl.classList.toggle('is-hidden', !visible);
	}

	getMediaTypeFromSlide(slide) {
		if (!slide) return 'image';
		if (slide.querySelector('product-model')) return 'model';
		const deferred = slide.querySelector('deferred-media');
		if (!deferred) return 'image';
		const template = deferred.querySelector('template');
		if (template?.content?.querySelector('iframe')) return 'external_video';
		if (template?.content?.querySelector('video')) return 'video';
		return 'video';
	}

	isVideoItem(item) {
		return item?.type === 'video' || item?.type === 'external_video';
	}

	collectItems() {
		const items = [];
		const thumbList = document.querySelector(`#Slider-Thumbnails-${this.sectionId}`);
		if (!thumbList) return items;

		thumbList.querySelectorAll('.thumbnail-list__item').forEach((li) => {
			const thumbImg = li.querySelector('img');
			const mediaId = li.dataset.target;
			const slide = this.getSlideEl(mediaId);
			const hasPlayBadge = Boolean(li.querySelector('.thumbnail__badge .icon-play'));
			let type = this.getMediaTypeFromSlide(slide);
			if (hasPlayBadge && type === 'image') {
				type = 'video';
			}
			const slideImg =
				slide?.querySelector('.product__modal-opener--image img') ||
				slide?.querySelector('.deferred-media__poster img') ||
				slide?.querySelector('img');
			const src = slideImg?.currentSrc || slideImg?.src || thumbImg?.currentSrc || thumbImg?.src;
			if (!src && type === 'image') return;

			items.push({
				type,
				mediaId,
				src: src || thumbImg?.currentSrc || thumbImg?.src || '',
				alt: thumbImg?.alt || slideImg?.alt || '',
				thumbSrc: thumbImg?.currentSrc || thumbImg?.src || src || '',
				hasPlayBadge: hasPlayBadge || Boolean(li.querySelector('.thumbnail__badge')),
				sourceDeferred: slide?.querySelector('deferred-media, product-model') || null,
			});
		});

		return items;
	}

	pauseGalleryMedia() {
		if (typeof window.pauseAllMedia === 'function') {
			window.pauseAllMedia();
		}
	}

	renderActiveMedia() {
		const item = this.items[this.activeIndex];
		if (!item) return;

		const isVideo = this.isVideoItem(item);
		this.root?.classList.toggle('pdp-image-gallery--media-video', isVideo);
		this.stage?.classList.toggle('is-media-video', isVideo);

		this.pauseGalleryMedia();

		if (isVideo) {
			this.setTransformVisible(false);

			if (this.videoEl) {
				this.videoEl.innerHTML = '';
				this.videoEl.hidden = false;
			}

			if (item.sourceDeferred) {
				const template = item.sourceDeferred.querySelector('template');
				if (template?.content?.firstElementChild) {
					const mediaNode = template.content.firstElementChild.cloneNode(true);
					if (mediaNode.nodeName === 'VIDEO' || mediaNode.nodeName === 'IFRAME') {
						mediaNode.removeAttribute('width');
						mediaNode.removeAttribute('height');
						mediaNode.style.maxWidth = '100%';
						mediaNode.style.maxHeight = '100%';
					}
					this.videoEl?.appendChild(mediaNode);
					if (mediaNode.nodeName === 'VIDEO') {
						mediaNode.controls = true;
						if (mediaNode.hasAttribute('autoplay')) {
							mediaNode.play().catch(() => {});
						}
					}
				}
			}
			return;
		}

		if (this.videoEl) {
			this.videoEl.innerHTML = '';
			this.videoEl.hidden = true;
		}
		this.setTransformVisible(true);
		if (this.mainImage) {
			this.mainImage.hidden = false;
			this.mainImage.src = item.src;
			this.mainImage.alt = item.alt;
		}
	}

	isThumbsScrollVertical() {
		return window.matchMedia('(min-width: 768px)').matches;
	}

	updateThumbScrollFade() {
		const wrap = this.thumbsWrap;
		if (!wrap) return;

		const vertical = this.isThumbsScrollVertical();
		const canScroll = vertical
			? wrap.scrollHeight > wrap.clientHeight + 1
			: wrap.scrollWidth > wrap.clientWidth + 1;
		const atEnd = vertical
			? wrap.scrollTop + wrap.clientHeight >= wrap.scrollHeight - 2
			: wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 2;

		wrap.classList.toggle('has-scroll-overflow', canScroll);
		wrap.classList.toggle('is-scroll-end', !canScroll || atEnd);
		this.thumbsFade?.classList.toggle('is-visible', canScroll && !atEnd);
	}

	renderThumbs() {
		if (!this.thumbsEl) return;
		const escapeAttr = (value) =>
			String(value ?? '')
				.replace(/&/g, '&amp;')
				.replace(/"/g, '&quot;')
				.replace(/</g, '&lt;');

		this.thumbsEl.innerHTML = this.items
			.map(
				(item, index) => `
			<button
				type="button"
				class="pdp-image-gallery__thumb${index === this.activeIndex ? ' is-active' : ''}${item.hasPlayBadge || this.isVideoItem(item) ? ' pdp-image-gallery__thumb--video' : ''}"
				data-pdp-gallery-thumb-index="${index}"
				aria-label="${escapeAttr(item.alt || `${this.isVideoItem(item) ? 'Video' : 'Image'} ${index + 1}`)}"
				aria-current="${index === this.activeIndex ? 'true' : 'false'}"
			>
				<img src="${escapeAttr(item.thumbSrc)}" alt="" loading="lazy" width="104" height="104" />
				${item.hasPlayBadge || this.isVideoItem(item) ? '<span class="pdp-image-gallery__thumb-play" aria-hidden="true"><svg class="icon icon-play" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 14" fill="currentColor" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M1.48177 0.814643C0.81532 0.448245 0 0.930414 0 1.69094V12.2081C0 12.991 0.858787 13.4702 1.52503 13.0592L10.5398 7.49813C11.1918 7.09588 11.1679 6.13985 10.4965 5.77075L1.48177 0.814643Z"/></svg></span>' : ''}
			</button>
		`
			)
			.join('');

		requestAnimationFrame(() => this.updateThumbScrollFade());
	}

	getTouchDistance(touches) {
		const [a, b] = touches;
		return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
	}

	clampScale(scale) {
		return Math.min(this.maxScale, Math.max(this.minScale, scale));
	}

	applyTransform() {
		if (!this.transformEl || this.isVideoItem(this.items[this.activeIndex])) return;
		this.transformEl.style.transform = `translate(${this.translate.x}px, ${this.translate.y}px) scale(${this.scale})`;
	}

	resetTransform() {
		this.scale = 1;
		this.translate = { x: 0, y: 0 };
		this.isDragging = false;
		this.isPinching = false;
		if (this.transformEl) {
			this.transformEl.style.transform = 'translate(0, 0) scale(1)';
		}
	}

	setIndex(index) {
		if (!this.items.length) return;
		this.activeIndex = Math.max(0, Math.min(index, this.items.length - 1));
		this.resetTransform();
		this.renderActiveMedia();
		this.renderThumbs();
	}

	zoomIn() {
		if (this.isVideoItem(this.items[this.activeIndex])) return;
		this.scale = this.clampScale(this.scale + this.scaleStep);
		this.applyTransform();
	}

	zoomOut() {
		if (this.isVideoItem(this.items[this.activeIndex])) return;
		this.scale = this.clampScale(this.scale - this.scaleStep);
		if (this.scale === 1) this.translate = { x: 0, y: 0 };
		this.applyTransform();
	}

	onTouchStart(event) {
		if (!this.isOpen() || !this.stage || this.isVideoItem(this.items[this.activeIndex])) return;

		if (event.touches.length === 2) {
			event.preventDefault();
			this.isPinching = true;
			this.isDragging = false;
			this.stage.classList.remove('is-dragging');
			this.pinchStartDistance = this.getTouchDistance(event.touches);
			this.pinchStartScale = this.scale;
			return;
		}

		if (event.touches.length === 1 && this.scale > 1) {
			this.onDragStart(event.touches[0]);
		}
	}

	onTouchMove(event) {
		if (!this.isOpen()) return;

		if (this.isPinching && event.touches.length >= 2) {
			event.preventDefault();
			const distance = this.getTouchDistance(event.touches);
			if (!this.pinchStartDistance) return;

			this.scale = this.clampScale(this.pinchStartScale * (distance / this.pinchStartDistance));
			if (this.scale === 1) this.translate = { x: 0, y: 0 };
			this.applyTransform();
			return;
		}

		if (this.isDragging && event.touches.length === 1) {
			event.preventDefault();
			this.onDragMove(event.touches[0]);
		}
	}

	onTouchEnd(event) {
		if (this.isPinching) {
			if (event.touches.length < 2) {
				this.isPinching = false;
				this.pinchStartDistance = 0;
				if (this.scale === 1) this.translate = { x: 0, y: 0 };
				this.applyTransform();
			}
			return;
		}

		if (event.touches.length === 0) {
			this.onDragEnd();
		}
	}

	onDragStart(event) {
		if (this.isPinching || this.scale <= 1 || !this.stage || this.isVideoItem(this.items[this.activeIndex])) return;
		this.isDragging = true;
		this.stage.classList.add('is-dragging');
		this.dragStart = {
			x: event.clientX,
			y: event.clientY,
			translateX: this.translate.x,
			translateY: this.translate.y,
		};
	}

	onDragMove(event) {
		if (!this.isDragging || this.isPinching) return;
		this.translate.x = this.dragStart.translateX + (event.clientX - this.dragStart.x);
		this.translate.y = this.dragStart.translateY + (event.clientY - this.dragStart.y);
		this.applyTransform();
	}

	onDragEnd() {
		this.isDragging = false;
		this.stage?.classList.remove('is-dragging');
	}

	open(index = 0) {
		this.items = this.collectItems();
		if (!this.items.length) return;

		this.root.hidden = false;
		this.root.setAttribute('aria-hidden', 'false');
		document.body.classList.add('pdp-image-gallery--open');
		this.setIndex(index);

		const firstThumb = this.thumbsEl?.querySelector('.pdp-image-gallery__thumb.is-active');
		firstThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
		requestAnimationFrame(() => this.updateThumbScrollFade());
	}

	close() {
		this.pauseGalleryMedia();
		if (this.videoEl) {
			this.videoEl.innerHTML = '';
			this.videoEl.hidden = true;
		}
		this.root?.classList.remove('pdp-image-gallery--media-video');
		this.stage?.classList.remove('is-media-video');
		this.root.hidden = true;
		this.root.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('pdp-image-gallery--open');
		this.resetTransform();
	}
}

let pdpImageGallery;

const initPdpThumbnailGallery = () => {
	const mediaGallery = document.querySelector('.product media-gallery');
	if (!mediaGallery || window.getPdpThumbnailClickMode() !== 'open_gallery') return;

	pdpImageGallery = pdpImageGallery || new PdpImageGallery();
	if (!pdpImageGallery?.root) return;

	mediaGallery.addEventListener(
		'click',
		(event) => {
			const thumbButton = event.target.closest('.thumbnail-list__item .thumbnail');
			if (!thumbButton) return;

			event.preventDefault();
			event.stopImmediatePropagation();

			const li = thumbButton.closest('.thumbnail-list__item');
			const thumbList = li?.parentElement;
			if (!li || !thumbList) return;

			const index = Array.from(thumbList.children).indexOf(li);
			pdpImageGallery.open(index);
		},
		true
	);
};

window.initPdpThumbnailGallery = initPdpThumbnailGallery;

const bootstrapPdpImageGallery = () => {
	if (window.getPdpThumbnailClickMode() === 'open_gallery') {
		initPdpThumbnailGallery();
	}
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', bootstrapPdpImageGallery);
} else {
	bootstrapPdpImageGallery();
}
